import { CalendarDays, CheckCircle2, LockKeyhole } from "lucide-react";
import type { GiveFeedbackData } from "../../lib/performance-lens/types.ts";
import styles from "./upcoming-cycle-banner.module.css";

type UpcomingCycle = NonNullable<GiveFeedbackData["upcomingCycle"]>;
type Context = "feedback" | "self" | "results" | "management";

const date = (value: string, includeYear = false) => new Intl.DateTimeFormat("en-IN", {
  day: "numeric", month: "short", ...(includeYear ? { year: "numeric" } : {}), timeZone: "Asia/Kolkata",
}).format(new Date(value.length === 10 ? `${value}T00:00:00+05:30` : value));

const dateRange = (start: string, end: string) => `${date(start)} – ${date(end, true)}`;

// Organisation cycles alternate between the first and second half of each month.
const nextWorkPeriod = (endDate: string) => {
  const [year, month, day] = endDate.split("-").map(Number);
  const start = new Date(Date.UTC(year, month - 1, day + 1));
  const end = start.getUTCDate() <= 15
    ? new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 15))
    : new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 0));
  return dateRange(start.toISOString().slice(0, 10), end.toISOString().slice(0, 10));
};

const messages: Record<Context, string> = {
  feedback: "Your review assignments will appear here when the feedback window opens.",
  self: "Your Self Check, if assigned, will be available during the feedback window.",
  results: "Your private results appear here only after the window closes and the cycle is published.",
  management: "The work period is for observation. Reviews and Self Check open after it ends.",
};

export default function UpcomingCycleBanner({ cycle, context, compact = false }: { cycle: UpcomingCycle; context: Context; compact?: boolean }) {
  const titleId = `upcoming-cycle-${context}`;
  return <section className={`${styles.banner} ${compact ? styles.compact : ""}`} aria-labelledby={titleId}>
    <div className={styles.intro}>
      <div className={styles.meta}>
        <span className={styles.kicker}><CalendarDays size={16} aria-hidden="true" /> Upcoming</span>
        <span className={styles.state}><LockKeyhole size={14} aria-hidden="true" /> Ratings open {date(cycle.feedbackOpensAt, true)}</span>
      </div>
      <h2 id={titleId}>Performance Cycle {cycle.number}</h2>
      <p>{messages[context]}</p>
    </div>
    <ol className={styles.timeline} aria-label={`Performance Cycle ${cycle.number} schedule`}>
      <li><span className={styles.stepIcon}><CalendarDays size={18} aria-hidden="true" /></span><div><small>Review cycle</small><strong>{dateRange(cycle.startDate, cycle.endDate)}</strong></div></li>
      <li><span className={styles.stepIcon}><CheckCircle2 size={18} aria-hidden="true" /></span><div><small>Feedback duration</small><strong>{dateRange(cycle.feedbackOpensAt, cycle.feedbackClosesAt)}</strong></div></li>
      <li><span className={styles.stepIcon}><CalendarDays size={18} aria-hidden="true" /></span><div><small>Next review cycle</small><strong>{nextWorkPeriod(cycle.endDate)}</strong></div></li>
    </ol>
  </section>;
}
